import {type Action, createReducer, on} from '@ngrx/store'
import {isToday} from '@common/utils/date.util'
import {type ClassifierSection, MuveValues} from '@models/classifier'
import {matches} from '@pages/home/home.utils'
import {ActivityOutletInfo} from '@service/activity.service'
import {MapMode} from '@service/map.service'
import * as HomeActionsTypes from './home.actions'
import type {Appointment} from '@models/appointment'
import type {Outlet} from '@models/outlet'
import type {Where} from '@models/where'

export type HomeState = {
  outletsLoaded: boolean
  outletsPlanned: Outlet[]
  outletsNotPlanned: Outlet[]
  outletsPlannedFiltered: Outlet[]
  outletsNotPlannedFiltered: Outlet[]
  allOutletFiltered: Outlet[]
  allFilteredOutletsMap: Outlet[]
  updatedOutlet: Outlet
  createdOutlet: Outlet
  total: number
  pagination: {
    pageNumber: number
    pageSize: number
  }
  filters: Where[]
  classifiers: {
    customers: ClassifierSection[]
    orders: ClassifierSection[]
    orderLine: ClassifierSection[]
    products: ClassifierSection[]
  }
  mapMode: MapMode
  isMapView: boolean
}

const initialState: HomeState = {
  outletsLoaded: false,
  outletsPlanned: [],
  outletsNotPlanned: [],
  outletsPlannedFiltered: [],
  outletsNotPlannedFiltered: [],
  allFilteredOutletsMap: [],
  allOutletFiltered: [],
  updatedOutlet: null,
  createdOutlet: null,
  total: 0,
  pagination: {
    pageNumber: 1,
    pageSize: 15,
  },
  filters: [],
  classifiers: {
    customers: [],
    orders: [],
    orderLine: [],
    products: [],
  },
  mapMode: MapMode.ROUTE,
  isMapView: false,
}

const reducer = createReducer(
  initialState,
  on(HomeActionsTypes.getClassifiersResponse, (state, {classifiers}) => {
    return {
      ...state,
      classifiers: {
        customers: getClasifiersVisibles(classifiers.customers),
        orders: getClasifiersVisibles(classifiers.orders),
        orderLine: getClasifiersVisibles(classifiers.orderLine),
        products: getClasifiersVisibles(classifiers.products),
      },
    }
  }),
  on(HomeActionsTypes.getOutletListRequest, state => ({
    ...state,
    pagination: {
      ...state.pagination,
      pageNumber: 1,
    },
  })),
  on(
    HomeActionsTypes.getOutletListResponse,
    (state, {allOutletFiltered, planned, notPlanned, total, outletsLoaded}) => ({
      ...state,
      outletsLoaded,
      allOutletFiltered,
      outletsPlanned: planned,
      outletsNotPlanned: notPlanned,
      outletsPlannedFiltered: planned,
      outletsNotPlannedFiltered: notPlanned,
      total,
      allFilteredOutletsMap: allOutletFiltered,
    })
  ),
  on(HomeActionsTypes.filterOutletsRequest, (state, {filters}) => ({
    ...state,
    filters,
    pagination: {
      ...state.pagination,
      pageNumber: 1,
    },
  })),
  on(HomeActionsTypes.filterOutletsResponse, (state, {notPlanned, allFilteredOutletsMap, total, planned}) => {
    const plannedOutlets = planned.filter(e => matches(e, state.filters, ActivityOutletInfo))

    const mapOutletsFiltered = [
      ...(Array.isArray(allFilteredOutletsMap) ? allFilteredOutletsMap : []),
      ...plannedOutlets.filter(o => !allFilteredOutletsMap?.find(o2 => o2.id === o.id)),
    ]

    return {
      ...state,
      outletsPlanned: planned,
      allOutletFiltered: notPlanned?.concat(plannedOutlets),
      outletsPlannedFiltered: plannedOutlets,
      outletsNotPlannedFiltered: notPlanned,
      total,
      allFilteredOutletsMap: mapOutletsFiltered,
    }
  }),
  on(HomeActionsTypes.getOutletListPaginateRequest, state => ({
    ...state,
    pagination: {
      ...state.pagination,
      pageNumber: state.pagination.pageNumber + 1,
    },
  })),
  on(HomeActionsTypes.getOutletListPaginateResponse, (state, {notPlanned}) => ({
    ...state,
    outletsNotPlannedFiltered: [...state.outletsNotPlannedFiltered, ...notPlanned],
  })),
  on(HomeActionsTypes.updateOutletOnList, (state, {outlet}) => {
    let newState = state
    state.outletsPlanned
      .filter(o => o.id === outlet.id)
      .forEach(outletState => {
        newState = updateStoreElement(newState, 'outletsPlanned', {
          ...outlet,
          orderGroup: outletState.orderGroup,
          appointmentDate: outletState.appointmentDate,
          appointmentId: outletState.appointmentId,
          fromGroup: outletState.fromGroup,
        })

        newState = updateStoreElement(newState, 'outletsPlannedFiltered', {
          ...outlet,
          orderGroup: outletState.orderGroup,
          appointmentDate: outletState.appointmentDate,
          appointmentId: outletState.appointmentId,
          fromGroup: outletState.fromGroup,
        })
      })

    newState = updateStoreElement(newState, 'outletsNotPlanned', outlet)

    newState = updateStoreElement(newState, 'outletsNotPlannedFiltered', outlet)

    newState = updateStoreElement(newState, 'allFilteredOutletsMap', outlet)
    newState = updateStoreElement(newState, 'allOutletFiltered', outlet)

    return {
      ...newState,
      updatedOutlet: newState.outletsPlanned.find(o => o.id === outlet.id) || outlet,
    }
  }),
  on(HomeActionsTypes.setMapMode, (state, {mode}) => ({
    ...state,
    mapMode: mode,
  })),
  on(HomeActionsTypes.setIsMapView, (state, {value}) => ({
    ...state,
    isMapView: value,
  })),
  on(HomeActionsTypes.setLoadedOutlets, state => ({
    ...state,
    outletsLoaded: false,
  })),
  on(HomeActionsTypes.resetHome, state => initialState),
  on(HomeActionsTypes.setCreatedOutlet, (state, {outlet}) =>
    outlet ?
      {
        ...state,
        outletsNotPlanned: [outlet, ...state.outletsNotPlanned],
        outletsNotPlannedFiltered: [outlet, ...state.outletsNotPlannedFiltered],
        allOutletFiltered: [outlet, ...state.allOutletFiltered],
        allFilteredOutletsMap: [outlet, ...state.allFilteredOutletsMap],
        createdOutlet: outlet,
        total: state.total + 1,
      }
    : {...state, createdOutlet: null}
  ),
  on(HomeActionsTypes.setOutletsListByAppointment, (state, {outlet, appointment, appointmentOld}) => {
    let total = state.total
    if (appointmentOld && isToday(new Date(appointmentOld.appointmentDate))) {
      state = parseOutletsListByRemovedAppointment(state, outlet, appointmentOld)
      if (appointment) state = parseOutletsListByRemovedAppointment(state, outlet, appointment)
      if (!state.outletsPlanned.some(o => o.id === outlet.id)) {
        total = total + 1
      }
    }
    if (appointment && isToday(new Date(appointment.appointmentDate))) {
      if (!state.outletsPlanned.some(o => o.id === outlet.id)) {
        total = total - 1
      }
      state = parseStateByNewAppointment(state, {
        ...outlet,
        appointmentId: outlet?.appointmentInfo?.appointmentId,
      })
    }
    return {
      ...state,
      total,
    }
  }),
  on(HomeActionsTypes.setMultipleOutletsListByAppointment, (state, {updates}) => {
    let newState = state
    let totalChange = 0

    for (const update of updates) {
      const result = applyOutletAppointmentUpdate(newState, update)
      newState = result.state
      totalChange += result.totalChange
    }

    return {
      ...newState,
      total: newState.total + totalChange,
    }
  }),
  on(HomeActionsTypes.resetHome, state => initialState)
)

export const homeReducer = (state: HomeState | undefined, action: Action) => reducer(state, action)

export const updateStoreElement = (state: any, array: string, newElement: any): any => {
  const index =
    array === 'outletsPlannedFiltered' || array === 'outletsPlanned' ?
      state[array].findIndex(
        (e: any) =>
          e.id === newElement.id &&
          e.orderGroup === newElement.orderGroup &&
          e.appointmentDate === newElement.appointmentDate
      )
    : state[array].findIndex((e: any) => e.id === newElement.id)

  if (index === -1) {
    return state
  }
  const item = state[array][index]

  if (matches(newElement, state.filters, ActivityOutletInfo)) {
    return {
      ...state,
      [array]: [...state[array].slice(0, index), updateElement(item, newElement), ...state[array].slice(index + 1)],
    }
  } else {
    return {
      ...state,
      [array]: [...state[array].slice(0, index), ...state[array].slice(index + 1)],
    }
  }
}

export const updateElement = (oldElement, newElement) => ({
  ...newElement,
  appointmentDate: oldElement.appointmentDate,
  fromGroup: oldElement.fromGroup,
})

const getClasifiersVisibles = (classifierSections: ClassifierSection[]): ClassifierSection[] =>
  classifierSections?.map(classifierSection => ({
    ...classifierSection,
    classifiers: classifierSection.classifiers.filter(
      clasifier => clasifier.muve && Number(clasifier.muve) !== MuveValues.NO
    ),
  }))

const parseOutletsListByRemovedAppointment = (state, outlet: Outlet, appointment: Appointment) => {
  outlet = {
    ...outlet,
    appointmentInfo: null,
    appointmentDate: null,
  }

  const outletPlanned = state.outletsPlanned.find(o => {
    return (
      o.id?.toString() === outlet.id?.toString() &&
      o.appointmentId?.toString() === appointment?.appointmentId?.toString()
    )
  })
  const indexPlanned = state.outletsPlanned.indexOf(outletPlanned)

  const outletPlannedFiltered = state.outletsPlannedFiltered.find(o => {
    return (
      o.id?.toString() === outlet.id?.toString() &&
      o.appointmentId?.toString() === appointment?.appointmentId?.toString()
    )
  })
  const indexPlannedFiltered = state.outletsPlannedFiltered.indexOf(outletPlannedFiltered)

  const indexPlanned2 = state.outletsPlanned.findIndex(
    o =>
      o.id?.toString() === outlet.id?.toString() &&
      o?.appointmentId?.toString() !== appointment?.appointmentId?.toString()
  )
  const indexAllOutletFiltered = state.allOutletFiltered.findIndex(o => o.id?.toString() === outlet.id?.toString())

  const outletsPlanned =
    indexPlanned === -1 ?
      state.outletsPlanned
    : [...state.outletsPlanned.slice(0, indexPlanned), ...state.outletsPlanned.slice(indexPlanned + 1)]
  const outletsPlannedFiltered =
    indexPlannedFiltered === -1 ?
      state.outletsPlannedFiltered
    : [
        ...state.outletsPlannedFiltered.slice(0, indexPlannedFiltered),
        ...state.outletsPlannedFiltered.slice(indexPlannedFiltered + 1),
      ]

  return {
    ...state,
    outletsPlanned,
    outletsNotPlanned: indexPlanned2 === -1 ? [...state.outletsNotPlanned, outlet] : state.outletsNotPlanned,
    outletsPlannedFiltered,
    outletsNotPlannedFiltered:
      indexPlanned2 > -1 ?
        state.outletsNotPlannedFiltered
      : appendIfMatchesFilters(state, state.outletsNotPlannedFiltered, outlet),
    allOutletFiltered:
      indexPlanned2 === -1 ?
        [
          ...state.allOutletFiltered.slice(0, indexAllOutletFiltered),
          outlet,
          ...state.allOutletFiltered.slice(indexAllOutletFiltered + 1),
        ]
      : state.allOutletFiltered,
    updatedOutlet: outlet,
  }
}

const appendIfMatchesFilters = (state, list: Outlet[], outlet: Outlet): Outlet[] =>
  matches(outlet, state.filters, ActivityOutletInfo) ? [...list, outlet] : list

const parseStateByNewAppointment = (state, outlet) => {
  const indexNotPlanned = state.outletsNotPlanned.findIndex(o => o.id === outlet.id)

  const indexAllOutletFiltered = state.allOutletFiltered.findIndex(o => o.id === outlet.id)

  const indexAllFilteredOutletsMap = state.allFilteredOutletsMap.findIndex(o => o.id === outlet.id)

  const indexNotPlannedFiltered = state.outletsNotPlannedFiltered.findIndex(o => o.id === outlet.id)
  return {
    ...state,
    outletsPlanned: [...state.outletsPlanned, outlet],
    outletsNotPlanned: [
      ...state.outletsNotPlanned.slice(0, indexNotPlanned),
      ...state.outletsNotPlanned.slice(indexNotPlanned + 1),
    ],
    outletsPlannedFiltered:
      matches(outlet, state.filters, ActivityOutletInfo) ?
        [...state.outletsPlannedFiltered, outlet]
      : state.outletsNotPlannedFiltered,
    outletsNotPlannedFiltered:
      indexNotPlannedFiltered === -1 ?
        state.outletsNotPlannedFiltered
      : [
          ...state.outletsNotPlannedFiltered.slice(0, indexNotPlannedFiltered),
          ...state.outletsNotPlannedFiltered.slice(indexNotPlannedFiltered + 1),
        ],
    allOutletFiltered: [
      ...state.allOutletFiltered.slice(0, indexAllOutletFiltered),
      outlet,
      ...state.allOutletFiltered.slice(indexAllOutletFiltered + 1),
    ],
    allFilteredOutletsMap: [
      ...state.allFilteredOutletsMap.slice(0, indexAllFilteredOutletsMap),
      outlet,
      ...state.allFilteredOutletsMap.slice(indexAllFilteredOutletsMap + 1),
    ],
    updatedOutlet: outlet,
  }
}

type AppointmentUpdate = {
  outlet: Outlet
  appointment?: Appointment
  appointmentOld?: Appointment
}

const applyOutletAppointmentUpdate = (
  state: HomeState,
  {outlet, appointment, appointmentOld}: AppointmentUpdate
): {state: HomeState; totalChange: number} => {
  let newState = state
  let totalChange = 0

  if (appointmentOld && isToday(new Date(appointmentOld.appointmentDate))) {
    newState = parseOutletsListByRemovedAppointment(newState, outlet, appointmentOld)
    if (appointment) newState = parseOutletsListByRemovedAppointment(newState, outlet, appointment)
    if (!newState.outletsPlanned.some(o => o.id === outlet.id)) totalChange += 1
  }

  if (appointment && isToday(new Date(appointment.appointmentDate))) {
    if (!newState.outletsPlanned.some(o => o.id === outlet.id)) totalChange -= 1
    newState = parseStateByNewAppointment(newState, {
      ...outlet,
      appointmentId: outlet?.appointmentInfo?.appointmentId,
    })
  }

  return {state: newState, totalChange}
}
